/**
 * The diner's Cart (ORDR-166), the app's own, replacing ORDR-165's interim
 * mount of the framework cart through `handoff`.
 *
 * Each line shows its size in its name, its answers summarised, its note, its
 * quantity and a way to remove it. The TOTAL IS THE SERVER QUOTE's, verbatim:
 * the Item screen's estimate is an aid while choosing and is never summed here.
 */

import type { ReactElement } from "react";
import { Button } from "@pirouz-ca/ui-components";
import type { OrderLine } from "./orderLine.js";

/** The most of one line, as the Item screen allows. */
const MAX_QUANTITY = 99;

export type CartTotal =
  | { readonly kind: "ok"; readonly text: string }
  /** No figure yet: pricing, waiting for a mode, or failed. `text` says which. */
  | { readonly kind: "pending"; readonly text: string };

export interface CartScreenProps {
  readonly lines: readonly OrderLine[];
  readonly total: CartTotal;
  readonly onQuantity: (lineId: string, quantity: number) => void;
  readonly onRemove: (lineId: string) => void;
  readonly onBack: () => void;
  readonly onCheckout: () => void;
}

const MUTED = "text-sm text-[var(--color-neutral-600)]";

export function CartScreen({ lines, total, onQuantity, onRemove, onBack, onCheckout }: CartScreenProps): ReactElement {
  return (
    <div className="flex flex-col text-[var(--color-neutral-900)]" data-testid="cart">
      <header className="flex items-center gap-2.5 border-b border-[var(--color-neutral-200)] px-5 py-3.5">
        <Button type="button" variant="secondary" aria-label="Back to the menu" onClick={onBack} className="h-11 w-11 rounded-full">
          ‹
        </Button>
        <h1 className="m-0 text-[22px] font-bold">Your cart</h1>
      </header>
      <main className="flex flex-col gap-3 px-5 py-4">
        {lines.length === 0 && <p className={`m-0 ${MUTED}`}>Your cart is empty.</p>}
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {lines.map((line) => (
            <li
              key={line.id}
              data-testid={`cart-line-${line.id}`}
              className="flex flex-col gap-2 rounded-[10px] border border-[var(--color-neutral-200)] px-3.5 py-3"
            >
              <span className="text-base font-semibold">{line.name}</span>
              {line.summary !== "" && <span className={MUTED}>{line.summary}</span>}
              {line.note !== "" && <span className={MUTED}>{`Note: ${line.note}`}</span>}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1 rounded-xl border border-[var(--color-neutral-300)]">
                  <button
                    type="button"
                    aria-label="One fewer"
                    disabled={line.quantity <= 1}
                    onClick={() => onQuantity(line.id, line.quantity - 1)}
                    className="h-11 w-11 text-[22px] disabled:opacity-50"
                  >
                    −
                  </button>
                  <span data-testid="quantity" className="min-w-6 text-center text-lg font-bold">
                    {line.quantity}
                  </span>
                  <button
                    type="button"
                    aria-label="One more"
                    disabled={line.quantity >= MAX_QUANTITY}
                    onClick={() => onQuantity(line.id, line.quantity + 1)}
                    className="h-11 w-11 text-[22px] disabled:opacity-50"
                  >
                    +
                  </button>
                </div>
                <Button type="button" variant="secondary" aria-label={`Remove ${line.name}`} onClick={() => onRemove(line.id)}>
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
        {lines.length > 0 && (
          <p data-testid="cart-total" className="m-0 flex justify-between text-base">
            {total.kind === "ok" ? (
              <>
                <span className="font-bold">Total</span>
                <span className="font-bold">{total.text}</span>
              </>
            ) : (
              <span className={MUTED}>{total.text}</span>
            )}
          </p>
        )}
      </main>
      {lines.length > 0 && (
        <footer className="sticky bottom-0 border-t border-[var(--color-neutral-200)] bg-[var(--color-neutral-50)] px-5 pt-3 pb-5">
          <button
            type="button"
            onClick={onCheckout}
            className="flex min-h-[52px] w-full items-center justify-center rounded-xl bg-[var(--color-primary-700)] text-[17px] font-bold text-[var(--color-neutral-50)]"
          >
            Checkout
          </button>
        </footer>
      )}
    </div>
  );
}
